import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useNotifications } from 'app/application/application'
import { useApplicationStore } from 'app/application/infrastructure'
import { useApi } from 'app/common/application'

import {
  AvailableSignalsResponse,
  CreateSignalContent,
  CreateSignalRequest,
  QuerySignalsRequest,
  QuerySignalsResponse,
} from '../types'

const useSignals = () => {
  const { create, read } = useApi()
  const { createNotification } = useNotifications()
  const { t } = useTranslation('error')
  const signals = useApplicationStore((state) => state.signals)
  const setSignals = useApplicationStore((state) => state.setSignals)
  const signalAvailability = useApplicationStore((state) => state.signalAvailability)
  const setSignalAvailability = useApplicationStore((state) => state.setSignalAvailability)

  const fetchAvailableSignals = useCallback(async () => {
    const result = await read<AvailableSignalsResponse>('/client/v1/signals/available')
    if (result.ok) {
      setSignalAvailability(result.val)
    } else {
      createNotification({
        message: result.val.message,
        title: t(`http.${result.val.code}`),
        type: 'error',
      })
    }
  }, [read, setSignalAvailability, createNotification, t])

  const fetchSignals = useCallback(async () => {
    const result = await create<QuerySignalsRequest, QuerySignalsResponse>('/signals/query', {
      content: {},
      pagination: {
        limit: 1000,
        page: 1,
      },
    })

    if (result.ok) {
      setSignals(result.val.data)
    } else {
      createNotification({
        message: result.val.message,
        title: t(`http.${result.val.code}`),
        type: 'error',
      })
    }
  }, [create, createNotification, setSignals, t])

  const createSignal = useCallback(
    async (content: CreateSignalContent) => {
      const result = await create<CreateSignalRequest, unknown>('/signals/create', { content })
      if (result.ok) await fetchSignals()
      return result
    },
    [create, fetchSignals],
  )

  return {
    createSignal,
    fetchAvailableSignals,
    signalAvailability,
    fetchSignals,
    signals,
  }
}

export default useSignals
